import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import './App.css';
import { useAuth } from './context/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Categories from './components/Categories';
import Budgets from './components/Budgets';
import Transactions from './components/Transactions';
import Accounts from './components/Accounts';
import Reports from './components/Reports';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import PrivacyPolicyPage from './pages/PrivacyPolicy';
import api from './utils/axios';
import {
  MdDashboard,
  MdAccountBalance,
  MdReceipt,
  MdAttachMoney,
  MdCategory,
  MdLogout,
  MdPrivacyTip,
  MdInsertChart
} from 'react-icons/md';

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', Icon: MdDashboard },
  { key: 'accounts', label: 'Accounts', Icon: MdAccountBalance },
  { key: 'transactions', label: 'Transactions', Icon: MdReceipt },
  { key: 'budgets', label: 'Budget', Icon: MdAttachMoney },
  { key: 'reports', label: 'Reports', Icon: MdInsertChart },
  { key: 'categories', label: 'Categories', Icon: MdCategory },
];

function MainDashboard() {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const { user, logout, checkAuthStatus } = useAuth();

  useEffect(() => {
    if (user && !user.privacy_policy_accepted) {
      setShowPrivacyModal(true);
    }
  }, [user]);

  const handleAcceptPrivacyPolicy = async () => {
    try {
      await api.post('/api/user/accept-privacy-policy');
      setShowPrivacyModal(false);
      await checkAuthStatus();
    } catch (error) {
      console.error('Error accepting privacy policy:', error);
      alert('Failed to accept privacy policy. Please try again.');
    }
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <button
          type="button"
          className="sidebar-brand"
          onClick={() => setActiveSection('dashboard')}
          aria-label="Go to Dashboard"
          title="Dashboard"
        >
          <img src="/icon.png" alt="$Ditto" className="sidebar-brand-icon" />
        </button>
        <nav className="sidebar-nav">
          {NAV_ITEMS.map(({ key, label, Icon }) => (
            <button
              key={key}
              className={`sidebar-link ${activeSection === key ? 'active' : ''}`}
              onClick={() => setActiveSection(key)}
            >
              <Icon className="sidebar-link-icon" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <img src={user.picture} alt={user.name} className="sidebar-user-avatar" />
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user.name}</div>
              <button className="sidebar-logout" onClick={logout}>
                <MdLogout /> Logout
              </button>
            </div>
          </div>
          <Link to="/privacy-policy" className="sidebar-link sidebar-link-small">
            <MdPrivacyTip className="sidebar-link-icon" />
            <span>Privacy</span>
          </Link>
        </div>
      </aside>

      <main className="main-area">
        <header className="top-bar">
          <h1 className="top-bar-title">
            {NAV_ITEMS.find(n => n.key === activeSection)?.label}
          </h1>
        </header>

        <div className="page">
          {activeSection === 'dashboard' && <Dashboard onNavigate={setActiveSection} />}
          {activeSection === 'accounts' && <Accounts />}
          {activeSection === 'transactions' && <Transactions />}
          {activeSection === 'budgets' && <Budgets />}
          {activeSection === 'reports' && <Reports />}
          {activeSection === 'categories' && <Categories />}
        </div>
      </main>

      {showPrivacyModal && (
        <PrivacyPolicyModal onAccept={handleAcceptPrivacyPolicy} />
      )}
    </div>
  );
}

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="app loading-screen">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route
        path="/"
        element={user ? <MainDashboard /> : <Login />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
